import { curveStepAfter } from 'd3-shape';
import '@d3fc/d3fc-element';
import {
    AnySelection,
    AnySeries,
    seriesSvgBar,
    seriesSvgLine,
    seriesSvgMulti,
} from '@d3fc/d3fc-series';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { deriveTime } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import {
    deriveBucketPercentOfMarketVolume,
    deriveBucketAvgPercentOfMarketVolume,
    deriveBucketMidTime,
    OrderFill,
    deriveMinParticipationRate,
    deriveMaxParticipationRate,
} from 'widgets/fillsGraph/fillsGraphData';
import { PercentageOfMarketVolumeData } from './PercentageOfMarketVolumeChart';
import './PercentageOfMarketVolumeChart.scss';

const BarPadding = 3;

const deriveBarWidth = (d: PercentageOfMarketVolumeData) => {
    return (d.bucketWidth - BarPadding) / 2;
};

const orderPercentOfMarketVolumeBars = () =>
    seriesSvgBar()
        .crossValue(deriveBucketMidTime)
        .mainValue(deriveBucketPercentOfMarketVolume)
        .bandwidth(deriveBarWidth)
        .decorate((selection: AnySelection) => {
            selection
                .select('path')
                .classed('percent-of-market-volume-bar', true);
        });

const avgPercentOfMarketVolumeLine = () =>
    seriesSvgLine()
        .crossValue(deriveBucketMidTime)
        .mainValue(deriveBucketAvgPercentOfMarketVolume)
        .curve(curveStepAfter)
        .decorate((selection: AnySelection) => {
            selection.classed('avg-percent-market-volume', true);
        });

const participationRateLine = (
    className: string,
    deriveParticipationRate: (d: OrderFill) => number | undefined
) =>
    seriesSvgLine()
        .crossValue(deriveTime)
        .mainValue(deriveParticipationRate)
        .curve(curveStepAfter)
        .decorate((selection: AnySelection) => {
            selection
                .classed(className, true)
                .classed('participation-rate', true);
        });

export const percentageOfMarketVolumeSeries = (
    yTicks: number,
    chartData: PercentageOfMarketVolumeData[]
) => {
    const minParticipationRateLine = participationRateLine(
        'min-participation',
        deriveMinParticipationRate
    );
    const maxParticipationRateLine = participationRateLine(
        'max-participation',
        deriveMaxParticipationRate
    );

    return seriesSvgMulti()
        .series([
            gridlines(yTicks),
            orderPercentOfMarketVolumeBars(),
            avgPercentOfMarketVolumeLine(),
            minParticipationRateLine,
            maxParticipationRateLine,
        ])
        .mapping((data: OrderFill[], index: number, series: AnySeries[]) => {
            switch (series[index]) {
                case minParticipationRateLine:
                    return data.filter(
                        (d) => d.minParticipationRate !== undefined
                    );
                case maxParticipationRateLine:
                    return data.filter(
                        (d) => d.maxParticipationRate !== undefined
                    );
                default:
                    return chartData;
            }
        });
};
